import React, { useState, useMemo, useRef, useImperativeHandle } from 'react';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import ChevronIcon from '@liquidweb-dev/undercurrent/icons/animated/Chevron';
import { styled } from '@liquidweb-dev/undercurrent/styles';

/**
 * @typedef {import('@liquidweb-dev/undercurrent').SxProps<import('@liquidweb-dev/undercurrent').Theme>} MuiSxStylesI
 */

/** @type MuiSxStylesI */
const buttonHoverStyles = {
	bgcolor: (theme) => theme.palette.uc.bg.medium,
	outline: 'none',
};

/** @type MuiSxStylesI */
const buttonStyles = {
	cursor: 'pointer',
	height: (theme) => theme.size.sm,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'stretch',
	color: (theme) => theme.palette.uc.text.weak,
	bgcolor: 'transparent',
	paddingY: 50,
	paddingX: 100,
	border: 'none',
	borderRadius: (theme) => theme.borderRadius.roundedSm,
	gap: 100,
	':hover': buttonHoverStyles,
	':active': buttonHoverStyles,
	':focus-visible': {
		outlineOffset: '2px',
		outlineWidth: '2px',
		outlineStyle: 'solid',
		outlineColor: (theme) => theme.palette.uc.highlight.border.main,
	},
};

const SSection = styled('section')({});
const SButton = styled('button')({});

/**
 * @typedef {React.ComponentProps<typeof SSection>} NavSectionPropsI
 * @property {string?} label
 */

/**
 * @param {NavSectionPropsI} props
 */
export const NavSection = React.forwardRef((props, ref) => {
	const { label, children, ...sectionProps } = props;

	const sectionRef = useRef();
	useImperativeHandle(ref, () => sectionRef.current);

	const ariaId = useMemo(() => {
		if (!label) return undefined;
		return `nav_${label}`;
	}, [label]);

	const [open, setOpen] = useState(true);

	return (
		<SSection {...sectionProps} ref={sectionRef}>
			{label && (
				<SButton
					onClick={() => setOpen((c) => !c)}
					sx={buttonStyles}
					aria-expanded={open}
					aria-controls={ariaId}
				>
					<Typography
						variant="body-xs"
						fontWeight={500}
						textTransform="uppercase"
					>
						{label}
					</Typography>
					<ChevronIcon open={open} sx={{ height: '16px', width: '16px' }} />
				</SButton>
			)}
			<Collapse in={open || !label} timeout={200} easing="linear" id={ariaId}>
				<Stack
					flexDirection="column"
					justifyContent="space-between"
					alignItems="stretch"
					gap={50}
					useFlexGap
					paddingTop={50}
				>
					{children}
				</Stack>
			</Collapse>
		</SSection>
	);
});
NavSection.displayName = 'NavSection';

export default NavSection;
